import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Save, Loader2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Sport prefix (slug first segment) -> label. 'org' bucket for org-scope groups.
const SPORTS = [
  { v: 'bk', l: 'Basketball' },
  { v: 'bb', l: 'Baseball' },
  { v: 'vl', l: 'Volleyball' },
  { v: 'sc', l: 'Soccer' },
  { v: 'fb', l: 'Football' },
  { v: 'org', l: 'Organization' },
];

const slugSport = (slug) => {
  const s = String(slug || '');
  if (s.startsWith('org-')) return 'org';
  return s.split('-')[0] || 'org';
};

// Fill a staff member into EXISTING staff-position slots. Each row picks a real
// position defined in the Staff Positions tab (unfilled, or already this person).
// One person may hold several positions (e.g. JVB Head Coach + VB Assistant).
export default function StaffAssignmentsEditor({ orgId, coachId }) {
  const [positions, setPositions] = useState([]);   // all staff_positions (enriched)
  const [groups, setGroups] = useState({});          // id -> group name
  const [rows, setRows] = useState([]);              // [{ sport, position_id }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, g] = await Promise.all([
        axios.get(`${API}/api/ol-canonical/positions/${orgId}?limit=500`).catch(() => ({ data: {} })),
        axios.get(`${API}/api/ol-canonical/staff-groups/${orgId}?limit=200`).catch(() => ({ data: {} })),
      ]);
      const plist = (p.data?.data || p.data || []);
      setPositions(plist);
      const gmap = {};
      (g.data?.data || g.data || []).forEach(x => { gmap[x.id] = x.name || x.title || x.slug || ''; });
      setGroups(gmap);
      // Seed rows from the slots this coach already fills.
      const mine = plist.filter(x => x.coach_id && x.coach_id === coachId);
      setRows(mine.map(x => ({ sport: slugSport(x.slug), position_id: x.id })));
    } finally {
      setLoading(false);
    }
  }, [orgId, coachId]);

  useEffect(() => { if (orgId && coachId) load(); }, [orgId, coachId, load]);

  const chosen = new Set(rows.map(r => r.position_id).filter(Boolean));
  const update = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const addRow = () => setRows([...rows, { sport: 'bk', position_id: '' }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const posLabel = (p) => {
    const grp = groups[p.staff_group_id] || '';
    const parts = [grp, p.title || 'Role'].filter(Boolean);
    let label = parts.join(' — ');
    if (p.season) label += ` · ${p.season}`;
    if (p.coach_id && p.coach_id === coachId) label += '  ✓';
    return label;
  };

  // Options for one row: positions in the chosen sport that are UNFILLED or held
  // by THIS coach, and not already picked in another row.
  const optionsFor = (row) => positions.filter(p =>
    slugSport(p.slug) === row.sport &&
    (!p.coach_id || p.coach_id === coachId) &&
    (!chosen.has(p.id) || p.id === row.position_id)
  );

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const payload = rows
        .filter(r => r.position_id)
        .map(r => ({ position_id: r.position_id }));
      await axios.put(`${API}/api/ol-canonical/coaches/${orgId}/${coachId}/assignments`, { assignments: payload });
      setSaved(true);
      await load();
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  if (!coachId) {
    return <p className="text-[11px] text-slate-400 italic" data-testid="assignments-need-save">Save the staff member first, then assign them to existing staff positions.</p>;
  }
  if (loading) return <div className="flex items-center gap-2 text-slate-400 text-xs"><Loader2 className="h-4 w-4 animate-spin" /> Loading positions…</div>;

  return (
    <div className="space-y-3" data-testid="staff-assignments-editor">
      {rows.length === 0 && (
        <p className="text-[11px] text-slate-500 italic">Not assigned to any staff position yet. Add one below.</p>
      )}
      {rows.map((r, i) => {
        const opts = optionsFor(r);
        return (
          <div key={i} className="grid grid-cols-12 gap-2 items-end bg-slate-900/40 p-2 rounded-lg border border-slate-700/50" data-testid={`assignment-row-${i}`}>
            <div className="col-span-3">
              <label className="text-[10px] text-slate-400 uppercase">Sport</label>
              <Select value={r.sport} onValueChange={(v) => update(i, { sport: v, position_id: '' })}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-8" data-testid={`assignment-sport-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{SPORTS.map(s => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-8">
              <label className="text-[10px] text-slate-400 uppercase">Staff Position (existing slot)</label>
              <Select value={r.position_id || undefined} onValueChange={(v) => update(i, { position_id: v })}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-slate-200 h-8" data-testid={`assignment-position-${i}`}><SelectValue placeholder="Select a staff position" /></SelectTrigger>
                <SelectContent>
                  {opts.length === 0 && <SelectItem value="__none" disabled>No open positions for this sport — create one in the Staff Positions tab</SelectItem>}
                  {opts.map(p => <SelectItem key={p.id} value={p.id}>{posLabel(p)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-1 flex justify-end">
              <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(i)} className="text-red-400 hover:bg-red-900/40 h-8 w-8" data-testid={`assignment-remove-${i}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        );
      })}
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={addRow} className="border-slate-600 text-slate-300" data-testid="assignment-add">
          <Plus className="h-4 w-4 mr-1" /> Add assignment
        </Button>
        <Button type="button" size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="assignment-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : (saved ? <Check className="h-4 w-4 mr-1" /> : <Save className="h-4 w-4 mr-1" />)}
          {saved ? 'Saved' : 'Save assignments'}
        </Button>
      </div>
      <p className="text-[10px] text-slate-500 italic">Pick existing positions (defined in the Staff Positions tab). One person can hold several. Assignments save immediately (separate from the Save Changes button above). Removing a row leaves the slot in place but unfilled.</p>
    </div>
  );
}
